import { createSlice } from "@reduxjs/toolkit";
import { Location } from "../../@types/location";
import { Organization } from "../../@types/organization";
import { assignLocations } from "./actions/assignLocations";
import { assignUsers } from "./actions/assignUsers";
import { assignUserToForm } from "./actions/assignUserToForm";
import { findAssignedLocations } from "./actions/findAssignedLocations";
import { findAssignedUsers } from "./actions/findAssignedUsers";
import { findCounter } from "./actions/findCounter";
import { findLocationList } from "./actions/findLocationList";
import { findUserList } from "./actions/findUserList";

interface UserRoleReduce {
  id: number;
  code: string;
  createdAt: number;
  updatedAt: number;
  user: {
    id: number;
    name: string;
    isSignatureEnabled: boolean;
    dni: string;
  },
  location: {
    id: number;
    name: string;
    code: string;
  },
  sourceOrganization: {
    id: number;
    name: string;
  },
  organization: {
    id: number;
    name: string;
  },
  role: {
    id: number;
    name: string;
  }
};

interface FormSubmittedState {
  searchUserText: string;
  searchSelectedUserText: string;
  searchLocationText: string;
  searchSelectedLocationText: string;
  userList: UserRoleReduce[];
  selectedUsers: UserRoleReduce[];
  assignedUsers:
  {
    id: number;
    user: {
      id: number;
      name: string;
    }
  }[];
  assignedLocations:
  {
    id: number;
    location: {
      id: number;
      name: string;
    }
  }[];
  locationList: Location[];
  selectedLocations: Location[];
  findUserListStatus: "idle" | "pending" | "succeeded" | "failed";
  findLocationListStatus: "idle" | "pending" | "succeeded" | "failed";
  assignUsersStatus: "idle" | "pending" | "succeeded" | "failed";
  assignUserToFormStatus: "idle" | "pending" | "succeeded" | "failed";
  assignLocationsStatus: "idle" | "pending" | "succeeded" | "failed";
  findAssignedUsersStatus: "idle" | "pending" | "succeeded" | "failed";
  findAssignedLocationsStatus: "idle" | "pending" | "succeeded" | "failed";
  findCounterStatus: "idle" | "pending" | "succeeded" | "failed";
  maxListAvailableIndex: number;
  createFilters: {
    organization: Organization;
    businessArea: Location;
  };
  counter: number;
}

const initialState = {
  searchUserText: "",
  searchSelectedUserText: "",
  searchLocationText: "",
  searchSelectedLocationText: "",
  userList: [],
  selectedUsers: [],
  locationList: [],
  selectedLocations: [],
  assignedUsers: [],
  assignedLocations: [],
  findUserListStatus: "idle",
  findLocationListStatus: "idle",
  assignUsersStatus: "idle",
  assignUserToFormStatus: "idle",
  assignLocationsStatus: "idle",
  findAssignedUsersStatus: "idle",
  findAssignedLocationsStatus: "idle",
  findCounterStatus: "idle",
  maxListAvailableIndex: 20,
  createFilters: {
    organization: {} as Organization,
    businessArea: {} as Location,
  },
  counter: 0,
} as FormSubmittedState;


const formSubmittedSlice = createSlice({
  name: "formSubmitted",
  initialState,
  reducers: {
    setInitialData(state) {
      state.searchUserText = "";
      state.searchSelectedUserText = "";
      state.searchLocationText = "";
      state.searchSelectedLocationText = "";
      state.maxListAvailableIndex = 20;
      state.assignUsersStatus = "idle";
      state.assignUserToFormStatus = "idle";
      state.assignLocationsStatus = "idle";
      state.createFilters.organization = {};
      state.createFilters.businessArea = {};
    },
    setSearchUserText(state, { payload }) {
      state.searchUserText = payload;
      state.maxListAvailableIndex = 20;
    },
    setSearchLocationText(state, { payload }) {
      state.searchLocationText = payload;
      state.maxListAvailableIndex = 20;
    },
    setSearchSelectedUsertext(state, { payload }) {
      state.searchSelectedUserText = payload;
    },
    setSearchSelectedLocationtext(state, { payload }) {
      state.searchSelectedLocationText = payload;
    },
    setMaxListAvailableIndex(state, { payload }) {
      state.maxListAvailableIndex = payload;
    },
    setOrganization(state, { payload }) {
      state.createFilters.organization = payload;
      state.maxListAvailableIndex = 20;
    },
    setBusinessArea(state, { payload }) {
      state.createFilters.businessArea = payload;
      state.maxListAvailableIndex = 20;
    },
    clearCreateFilters(state) {
      state.createFilters = {
        organization: {} as Organization,
        businessArea: {} as Location,
      };
      state.maxListAvailableIndex = 20;
    },
    setPreselectUser(state, { payload }) {
      state.selectedUsers = [...state.selectedUsers, payload];
    },
    setPreselectAllUsers(state, { payload }) {
      state.selectedUsers = [...state.selectedUsers, ...payload];
    },
    setDeselectUser(state, { payload }) {
      state.selectedUsers = state.selectedUsers.filter(({ id }) => id !== payload.id);
    },
    setDeselectAllUsers(state) {
      state.selectedUsers = [];
    },
    setPreselectLocation(state, { payload }) {
      state.selectedLocations = [...state.selectedLocations, payload];
    },
    setPreselectAllLocations(state, { payload }) {
      state.selectedLocations = [...state.selectedLocations, ...payload];
    },
    setDeselectLocation(state, { payload }) {
      state.selectedLocations = state.selectedLocations.filter(({ id }) => id !== payload.id);
    },
    setDeselectAllLocations(state) {
      state.selectedLocations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findUserList.pending, (state) => {
        state.findUserListStatus = "pending";
      })
      .addCase(findUserList.fulfilled, (state, { payload }) => {
        state.userList = payload;
        state.findUserListStatus = "succeeded";
      })
      .addCase(findUserList.rejected, (state) => {
        state.userList = [];
        state.findUserListStatus = "failed";
      })
      .addCase(findLocationList.pending, (state) => {
        state.findLocationListStatus = "pending";
        state.locationList = [];
      })
      .addCase(findLocationList.fulfilled, (state, { payload }) => {
        state.locationList = payload;
        state.findLocationListStatus = "succeeded";
      })
      .addCase(findLocationList.rejected, (state) => {
        state.locationList = [];
        state.findLocationListStatus = "failed";
      })
      .addCase(findAssignedUsers.pending, (state) => {
        state.findAssignedUsersStatus = "pending";
        state.selectedUsers = [];
        state.assignedUsers = [];
      })
      .addCase(findAssignedUsers.fulfilled, (state, { payload }) => {
        state.findAssignedUsersStatus = "succeeded";
        state.selectedUsers = payload.selected;
        state.assignedUsers = payload.assigned;
      })
      .addCase(findAssignedUsers.rejected, (state) => {
        state.findAssignedUsersStatus = "failed";
        state.selectedUsers = [];
        state.assignedUsers = [];
      })
      .addCase(findAssignedLocations.pending, (state) => {
        state.findAssignedLocationsStatus = "pending";
        state.selectedLocations = [];
        state.assignedLocations = [];
      })
      .addCase(findAssignedLocations.fulfilled, (state, { payload }) => {
        state.findAssignedLocationsStatus = "succeeded";
        state.selectedLocations = payload.selected;
        state.assignedLocations = payload.assigned;
      })
      .addCase(findAssignedLocations.rejected, (state) => {
        state.findAssignedLocationsStatus = "failed";
        state.selectedLocations = [];
        state.assignedLocations = [];
      })
      .addCase(assignUsers.pending, (state) => {
        state.assignUsersStatus = "pending";
      })
      .addCase(assignUsers.fulfilled, (state) => {
        state.assignUsersStatus = "succeeded";
      })
      .addCase(assignUsers.rejected, (state) => {
        state.assignUsersStatus = "failed";
      })
      .addCase(assignLocations.pending, (state) => {
        state.assignLocationsStatus = "pending";
      })
      .addCase(assignLocations.fulfilled, (state) => {
        state.assignLocationsStatus = "succeeded";
      })
      .addCase(assignLocations.rejected, (state) => {
        state.assignLocationsStatus = "failed";
      })
      .addCase(findCounter.pending, (state) => {
        state.counter = 0;
        state.findCounterStatus = "pending";
      })
      .addCase(findCounter.fulfilled, (state, { payload }) => {
        state.counter = payload;
        state.findCounterStatus = "succeeded";
      })
      .addCase(findCounter.rejected, (state) => {
        state.counter = 0;
        state.findCounterStatus = "failed";
      })
      .addCase(assignUserToForm.pending, (state) => {
        state.assignUserToFormStatus = "pending";
      })
      .addCase(assignUserToForm.fulfilled, (state) => {
        state.assignUserToFormStatus = "succeeded";
      })
      .addCase(assignUserToForm.rejected, (state) => {
        state.assignUserToFormStatus = "failed";
      })
      ;
  },
});

export const
  {
    setInitialData,
    setSearchUserText,
    setSearchSelectedUsertext,
    setSearchLocationText,
    setSearchSelectedLocationtext,
    setMaxListAvailableIndex,
    setBusinessArea,
    setOrganization,
    clearCreateFilters,
    setDeselectAllUsers,
    setDeselectUser,
    setPreselectAllUsers,
    setPreselectUser,
    setDeselectLocation,
    setPreselectLocation,
    setDeselectAllLocations,
    setPreselectAllLocations
  } = formSubmittedSlice.actions;

export default formSubmittedSlice.reducer;
